/**
 * EventsHub — public browse page at `/events`.
 *
 * Filterable by event_type, subject, firm, year_group. Left sidebar
 * shows facet counts from `/api/events/facets`.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import EventCard from "@/components/app/EventCard";
import { Filter, X, Sparkles } from "lucide-react";

const EVENT_TYPE_LABEL = {
  insight_week: "Insight Week",
  internship: "Internship",
  spring_week: "Spring Week",
  open_day: "Open Day",
  networking: "Networking",
  webinar: "Webinar",
  conference: "Conference",
};

const YEAR_GROUPS = [
  { key: "yr12", label: "Year 12" },
  { key: "yr13", label: "Year 13" },
  { key: "ug1", label: "UG Year 1" },
  { key: "ug2", label: "UG Year 2" },
  { key: "ug3", label: "UG Year 3" },
  { key: "penultimate", label: "Grad Yr 1" },
  { key: "finalist", label: "Grad Yr 2" },
];

export default function EventsHub() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(
    () => ({
      event_type: params.get("event_type") || "",
      subject: params.get("subject") || "",
      firm_slug: params.get("firm_slug") || "",
      year_group: params.get("year_group") || "",
      city: params.get("city") || "",
    }),
    [params],
  );

  const [events, setEvents] = useState([]);
  const [facets, setFacets] = useState({ event_types: [], subjects: [], firms: [], cities: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
    Promise.all([
      api.get(`/events?${q.toString()}`),
      api.get(`/events/facets`),
    ])
      .then(([e, f]) => {
        setEvents(e.data.events || []);
        setFacets(f.data || { event_types: [], subjects: [], firms: [], cities: [] });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const activeFilters = Object.entries(filters).filter(([, v]) => v);

  return (
    <main className="max-w-6xl mx-auto px-6 py-10" data-testid="events-hub-root">
      <Helmet>
        <title>Firm insight weeks, internships & spring weeks · WhichUniversityAdvisor</title>
        <meta
          name="description"
          content="Every upcoming insight week, spring week, internship and open day from firms with a Verified Partner profile. Filter by event type, subject, and firm."
        />
      </Helmet>

      <header className="mb-8">
        <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
          Firm events
        </div>
        <h1 className="mt-1 font-editorial text-4xl sm:text-5xl text-[#002147]">
          Upcoming from firms hiring right now
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed">
          Insight weeks, spring weeks, internships, open days, and networking
          events from firms who've published them on WUA. Every event is
          reviewed by our editorial team before it appears here.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside data-testid="events-hub-filters" className="space-y-6 self-start">
          <FilterSection title="Event type">
            {facets.event_types.length === 0 && (
              <div className="text-xs text-stone-400 italic">No events yet.</div>
            )}
            {facets.event_types.map((t) => (
              <FilterRow
                key={t.key}
                testId={`events-filter-type-${t.key}`}
                label={EVENT_TYPE_LABEL[t.key] || t.key}
                count={t.count}
                active={filters.event_type === t.key}
                onToggle={() =>
                  setFilter("event_type", filters.event_type === t.key ? "" : t.key)
                }
              />
            ))}
          </FilterSection>

          {facets.subjects.length > 0 && (
            <FilterSection title="Subject">
              {facets.subjects.slice(0, 10).map((s) => (
                <FilterRow
                  key={s.key}
                  testId={`events-filter-subject-${s.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  label={s.key}
                  count={s.count}
                  active={filters.subject === s.key}
                  onToggle={() =>
                    setFilter("subject", filters.subject === s.key ? "" : s.key)
                  }
                />
              ))}
            </FilterSection>
          )}

          {facets.firms.length > 0 && (
            <FilterSection title="Firm">
              {facets.firms.slice(0, 10).map((f) => (
                <FilterRow
                  key={f.slug}
                  testId={`events-filter-firm-${f.slug}`}
                  label={f.firm_name || f.slug}
                  count={f.count}
                  active={filters.firm_slug === f.slug}
                  onToggle={() =>
                    setFilter("firm_slug", filters.firm_slug === f.slug ? "" : f.slug)
                  }
                />
              ))}
            </FilterSection>
          )}

          <FilterSection title="Year group">
            {YEAR_GROUPS.map((yg) => (
              <FilterRow
                key={yg.key}
                testId={`events-filter-yg-${yg.key}`}
                label={yg.label}
                active={filters.year_group === yg.key}
                onToggle={() =>
                  setFilter("year_group", filters.year_group === yg.key ? "" : yg.key)
                }
              />
            ))}
          </FilterSection>
        </aside>

        <section>
          {/* By-city strip — horizontal chip row over the results
              column, populated from the /events/facets `cities`
              pipeline (approved, non-expired, non-virtual, non-empty
              city). Adds a canonical location filter next to the
              existing type/subject/firm/year_group facets. */}
          {facets.cities && facets.cities.length > 0 && (
            <div
              data-testid="events-hub-city-strip"
              className="mb-4 -mx-1 overflow-x-auto"
            >
              <div className="flex items-center gap-1.5 px-1 min-w-max">
                <span className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500 pr-1">
                  By city:
                </span>
                <button
                  type="button"
                  onClick={() => setFilter("city", "")}
                  data-testid="events-filter-city-all"
                  className={
                    "text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap " +
                    (!filters.city
                      ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                      : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50")
                  }
                >
                  All cities
                </button>
                {facets.cities.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`events-filter-city-${c.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() =>
                      setFilter("city", filters.city === c.key ? "" : c.key)
                    }
                    className={
                      "text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap " +
                      (filters.city === c.key
                        ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                        : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50")
                    }
                  >
                    {c.key} <span className="opacity-70">· {c.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeFilters.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs" data-testid="events-hub-active-filters">
              <span className="text-stone-500">Filters:</span>
              {activeFilters.map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k, "")}
                  className="inline-flex items-center gap-1 rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] text-[#4C1D95] px-2 py-0.5 hover:bg-[#EDE4FF]"
                >
                  {v} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="text-stone-500 text-sm">Loading events…</div>
          ) : events.length === 0 ? (
            <EmptyState hasFilters={activeFilters.length > 0} />
          ) : (
            <div className="space-y-4" data-testid="events-hub-list">
              {events.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function FilterSection({ title, children }) {
  return (
    <div>
      <h3 className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500 mb-2 flex items-center gap-1.5">
        <Filter className="h-3 w-3" /> {title}
      </h3>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

function FilterRow({ label, count, active, onToggle, testId }) {
  return (
    <li>
      <button
        data-testid={testId}
        onClick={onToggle}
        className={
          "w-full text-left flex items-center justify-between px-2 py-1 rounded-md text-xs transition-colors " +
          (active
            ? "bg-[#4C1D95] text-white font-semibold"
            : "hover:bg-stone-100 text-stone-700")
        }
      >
        <span className="truncate">{label}</span>
        {count !== undefined && <span className="ml-2 opacity-70">{count}</span>}
      </button>
    </li>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center">
      <Sparkles className="h-6 w-6 text-stone-400 mx-auto mb-3" />
      <h3 className="text-lg font-editorial text-stone-900">
        {hasFilters ? "No events match those filters" : "No events published yet"}
      </h3>
      <p className="text-sm text-stone-600 max-w-md mx-auto mt-2 leading-relaxed">
        {hasFilters
          ? "Try clearing a filter — or check back next week, we approve new firm submissions daily."
          : "Firms are just starting to publish here. Want to be first? "}
        {!hasFilters && (
          <Link to="/firm-guides" className="text-[#4C1D95] font-medium hover:underline">
            Claim your firm's profile →
          </Link>
        )}
      </p>
    </div>
  );
}
